/**
 * TopBanksList Component
 * Displays top banks by offer count
 */

import { Card, CardContent, CardHeader, CardTitle } from "@/shared/components/ui";
import { Building2 } from "lucide-react";

interface BankData {
  name: string;
  count: number;
  percentage: number;
  avgRrso?: number;
}

interface TopBanksListProps {
  banks: BankData[];
  isLoading?: boolean;
}

export function TopBanksList({ banks, isLoading = false }: TopBanksListProps) {
  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Building2 className="w-5 h-5" />
            Top banki
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {[1, 2, 3, 4, 5].map((i) => (
              <div key={i} className="flex items-center gap-3 animate-pulse">
                <div className="w-6 h-6 bg-gray-200 rounded" />
                <div className="flex-1 h-4 bg-gray-200 rounded" />
                <div className="w-12 h-4 bg-gray-200 rounded" />
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    );
  }

  if (banks.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Building2 className="w-5 h-5" />
            Top banki
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-gray-500 text-center py-4">
            Brak danych o bankach
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Building2 className="w-5 h-5" />
          Top banki
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-3">
          {banks.slice(0, 5).map((bank, index) => (
            <div key={bank.name} className="flex items-center gap-3">
              {/* Rank */}
              <div
                className={`w-6 h-6 rounded flex items-center justify-center text-xs font-medium ${
                  index === 0
                    ? "bg-warning-100 text-warning-700"
                    : index === 1
                    ? "bg-gray-200 text-gray-700"
                    : index === 2
                    ? "bg-orange-100 text-orange-700"
                    : "bg-gray-100 text-gray-500"
                }`}
              >
                {index + 1}
              </div>

              {/* Bank name */}
              <div className="flex-1 min-w-0">
                <p className="font-medium text-gray-900 truncate">{bank.name}</p>
                {bank.avgRrso && (
                  <p className="text-xs text-gray-500">
                    Śr. RRSO: {bank.avgRrso.toFixed(1)}%
                  </p>
                )}
              </div>

              {/* Percentage bar */}
              <div className="w-24">
                <div className="flex items-center gap-2">
                  <div className="flex-1 h-2 bg-gray-100 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-primary-500 rounded-full transition-all"
                      style={{ width: `${bank.percentage}%` }}
                    />
                  </div>
                  <span className="text-sm text-gray-600 w-10 text-right">
                    {bank.percentage}%
                  </span>
                </div>
              </div>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}
